/**
 * Shared vehicle validation and normalization helpers
 * Used by both Create Customer flow and Edit Customer flow
 */

/**
 * Validates a single vehicle object
 * @param {Object} vehicle - Vehicle object with make, model, color, license_plate, state
 * @returns {Object} - { isValid: boolean, errors: { field: 'error message' } }
 */
export function validateVehicle(vehicle) {
  const errors = {};
  
  if (!vehicle.make || !vehicle.make.trim()) {
    errors.make = 'Make is required';
  }
  
  if (!vehicle.model || !vehicle.model.trim()) {
    errors.model = 'Model is required';
  }
  
  if (!vehicle.color || !vehicle.color.trim()) {
    errors.color = 'Color is required';
  }
  
  if (!vehicle.license_plate || !vehicle.license_plate.trim()) {
    errors.license_plate = 'License plate is required';
  }
  
  if (!vehicle.state || !vehicle.state.trim()) {
    errors.state = 'State is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Normalizes a single vehicle object for API payload
 * - Trims make, model, color
 * - Uppercases license_plate and state
 * @param {Object} vehicle - Vehicle object
 * @returns {Object} - Normalized vehicle object
 */
export function normalizeVehicle(vehicle) {
  return {
    make: vehicle.make.trim(),
    model: vehicle.model.trim(),
    color: vehicle.color.trim(),
    license_plate: vehicle.license_plate.trim().toUpperCase(),
    state: vehicle.state.trim().toUpperCase()
  };
}

/**
 * Validates and normalizes an array of vehicles
 * @param {Array} vehicles - Array of vehicle objects
 * @returns {Object} - { isValid: boolean, errors: { index: { field: 'error' } }, normalized: Array }
 */
export function validateAndNormalizeVehicles(vehicles) {
  const errors = {};
  const normalized = [];
  
  vehicles.forEach((vehicle, index) => {
    const validation = validateVehicle(vehicle);
    
    if (!validation.isValid) {
      errors[index] = validation.errors;
    } else {
      normalized.push(normalizeVehicle(vehicle));
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0 && normalized.length > 0,
    errors,
    normalized
  };
}

/**
 * Builds the API payload for vehicle creation
 * @param {Object} params - { customer_id, company, admin_id, vehicles }
 * @returns {Object} - API payload
 */
export function buildVehiclePayload({ customer_id, company, admin_id, vehicles }) {
  const result = validateAndNormalizeVehicles(vehicles);
  
  if (!result.isValid) {
    return { error: result.errors, payload: null };
  }
  
  return {
    error: null,
    payload: {
      customer_id,
      company,
      admin_id,
      data_list: result.normalized
    }
  };
}

